import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Lock, Save, Image as ImageIcon, Building2, Palette, Ticket, Trash2, CreditCard, Wifi, Calendar, Package } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import apiClient from '@/utils/api';

const SettingsPage = () => {
  // Settings state
  const [businessName, setBusinessName] = useState('');
  const [themeColor, setThemeColor] = useState('#f97316');
  const [businessNameColor, setBusinessNameColor] = useState('#ffffff');
  const [appTheme, setAppTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [posType, setPosType] = useState('harici');
  const [posIp, setPosIp] = useState('');
  const [isTestingPOS, setIsTestingPOS] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [reservationEnabled, setReservationEnabled] = useState(false);
  const [reservationCode, setReservationCode] = useState('');
  const [smsProvider, setSmsProvider] = useState('simulasyon');
  const [smsUsername, setSmsUsername] = useState('');
  const [smsPassword, setSmsPassword] = useState('');
  const [smsHeader, setSmsHeader] = useState('');
  const [inventoryEnabled, setInventoryEnabled] = useState(false);

  // Password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Logo state
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [newCouponCode, setNewCouponCode] = useState('');
  const [newCouponType, setNewCouponType] = useState('percentage');
  const [newCouponValue, setNewCouponValue] = useState('');
  const [newCouponExpiry, setNewCouponExpiry] = useState('');
  const [newCouponMaxUses, setNewCouponMaxUses] = useState('');
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchCoupons();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/tenant/settings');
      setBusinessName(response.data.business_name || '');
      setThemeColor(response.data.theme_color || '#f97316');
      setBusinessNameColor(response.data.business_name_color || '#ffffff');
      setPosType(response.data.pos_type || 'harici');
      setPosIp(response.data.pos_ip || '');
      const isResEnabled = response.data.reservation_enabled || false;
      setReservationEnabled(isResEnabled);
      localStorage.setItem('reservation_enabled', isResEnabled ? 'true' : 'false');
      const isInvEnabled = response.data.inventory_enabled || false;
      setInventoryEnabled(isInvEnabled);
      localStorage.setItem('inventory_enabled', isInvEnabled ? 'true' : 'false');
      setReservationCode(response.data.reservation_code || '');
      setSmsProvider(response.data.sms_provider || 'simulasyon');
      setSmsUsername(response.data.sms_username || '');
      setSmsPassword(response.data.sms_password || '');
      setSmsHeader(response.data.sms_header || '');
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await apiClient.get('/coupons');
      setCoupons(response.data);
    } catch (error) {
      console.error('Kuponlar yüklenemedi:', error);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!newCouponCode || !newCouponValue) return;
    
    try {
      setIsCreatingCoupon(true);
      const payload = {
        code: newCouponCode.toUpperCase(),
        discount_type: newCouponType,
        discount_value: parseFloat(newCouponValue),
        expires_at: newCouponExpiry ? new Date(newCouponExpiry).toISOString() : null,
        max_uses: newCouponMaxUses ? parseInt(newCouponMaxUses) : null
      };
      await apiClient.post('/coupons', payload);
      toast.success('Kupon başarıyla oluşturuldu');
      setNewCouponCode('');
      setNewCouponValue('');
      setNewCouponExpiry('');
      setNewCouponMaxUses('');
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kupon oluşturulurken hata oluştu');
    } finally {
      setIsCreatingCoupon(false);
    }
  };

  const handleDeleteCoupon = async (id) => {
    try {
      await apiClient.delete(`/coupons/${id}`);
      toast.success('Kupon silindi');
      fetchCoupons();
    } catch (error) {
      toast.error('Kupon silinirken hata oluştu');
    }
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    try {
      setIsSavingSettings(true);
      await apiClient.put('/tenant/settings', {
        business_name: businessName,
        theme_color: themeColor,
        business_name_color: businessNameColor,
        pos_type: posType,
        pos_ip: posIp,
        reservation_enabled: reservationEnabled,
        reservation_code: reservationCode,
        sms_provider: smsProvider,
        sms_username: smsUsername,
        sms_password: smsPassword,
        sms_header: smsHeader,
        inventory_enabled: inventoryEnabled
      });
      localStorage.setItem('theme', appTheme);
      localStorage.setItem('business_name', businessName);
      localStorage.setItem('reservation_enabled', reservationEnabled ? 'true' : 'false');
      localStorage.setItem('inventory_enabled', inventoryEnabled ? 'true' : 'false');
      if (appTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      toast.success('İşletme ayarları başarıyla kaydedildi.');
      setTimeout(() => window.location.reload(), 1000); // Reload to apply theme everywhere
    } catch (error) {
      toast.error('Ayarlar kaydedilirken bir hata oluştu.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor!');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır!');
      return;
    }

    try {
      setIsChangingPassword(true);
      await apiClient.put('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword
      });
      toast.success('Şifreniz başarıyla güncellendi. Lütfen tekrar giriş yapın.');
      setTimeout(() => {
        localStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_token');
        window.location.href = '#/login';
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre güncellenirken bir hata oluştu.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo boyutu 2MB dan küçük olmalıdır.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setLogoBase64(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoBase64) {
      toast.error('Lütfen önce bir logo seçin.');
      return;
    }

    try {
      setIsUploadingLogo(true);
      await apiClient.put('/tenant/logo', {
        logo_base64: logoBase64
      });
      toast.success('Logonuz başarıyla kaydedildi. Müşteri menüsünde anında görünecektir.');
    } catch (error) {
      toast.error('Logo kaydedilirken bir hata oluştu.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handlePOSTest = async () => {
    setIsTestingPOS(true);
    try {
      if (window.electron && window.electron.testPOSConnection) {
        const result = await window.electron.testPOSConnection(posType, posIp);
        if (result.success) {
          toast.success(result.message, { duration: 5000 });
        } else {
          toast.error(result.message, { duration: 5000 });
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (posType === 'harici') {
          toast.success('Harici POS modu seçili. (Web / Bulut Simülasyonu)');
        } else if (!posIp && posType !== 'beko') {
          toast.error('Lütfen geçerli bir IP adresi veya Terminal ID girin.');
        } else {
          toast.success(`${posType.toUpperCase()} sanal entegrasyonu başarılı! (Fiziki port erişimi için Windows uygulamasını kullanın)`, { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error('POS bağlantı testi sırasında hata oluştu.');
    } finally {
      setIsTestingPOS(false);
    }
  };

  const themeColors = [
    { name: 'Turuncu (Varsayılan)', value: '#f97316' },
    { name: 'Kırmızı', value: '#ef4444' },
    { name: 'Mavi', value: '#3b82f6' },
    { name: 'Yeşil', value: '#22c55e' },
    { name: 'Mor', value: '#a855f7' },
    { name: 'Siyah', value: '#171717' }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ayarlar
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">İşletme ayarlarınızı ve güvenliğinizi yönetin</p>
          </div>
        </div>

        <Tabs defaultValue="business" className="w-full space-y-6">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-5 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl h-auto gap-1">
            <TabsTrigger value="business" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-3 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-2">
              <Building2 size={18} />
              İşletme Bilgileri
            </TabsTrigger>
            <TabsTrigger value="pos" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-3 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-2">
              <CreditCard size={18} />
              Yazar Kasa & POS
            </TabsTrigger>

            <TabsTrigger value="inventory" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-3 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-2">
              <Package size={18} />
              Stok & Depo
            </TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-3 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-2">
              <Lock size={18} />
              Şifre Değiştir
            </TabsTrigger>
            <TabsTrigger value="coupons" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-3 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-2">
              <Ticket size={18} />
              Kuponlar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="business" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Business Details Card */}
              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <Building2 className="text-orange-500" size={24} />
                    İşletme Bilgileri ve Tema
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSettingsSave} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-slate-700 dark:text-slate-300">İşletme / Restoran Adı</Label>
                        <Input
                          type="text"
                          placeholder="Örn: Cularix Kafe"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                          required
                        />
                        <p className="text-xs text-slate-500">Bu isim müşteri menüsünde logonuzun yanında görünecektir.</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 dark:text-slate-300">Görünüm Teması</Label>
                        <select
                          value={appTheme}
                          onChange={(e) => setAppTheme(e.target.value)}
                          className="w-full h-10 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="dark">Koyu Tema (Varsayılan)</option>
                          <option value="light">Açık Tema</option>
                        </select>
                        <p className="text-xs text-slate-500">Tüm sistem arayüzünün rengini belirler.</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                          <Palette size={16} /> Tema Rengi
                        </Label>
                        <div className="flex flex-wrap gap-3">
                          {themeColors.map(color => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => setThemeColor(color.value)}
                              className={`w-10 h-10 rounded-full border-2 transition-all ${themeColor === color.value ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            />
                          ))}
                          <div className="relative">
                            <input
                              type="color"
                              value={themeColor}
                              onChange={(e) => setThemeColor(e.target.value)}
                              className="w-10 h-10 rounded-full border-2 border-slate-300 dark:border-slate-700 cursor-pointer overflow-hidden p-0"
                              title="Özel Renk Seç"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">Müşteri menüsündeki butonlar ve fiyatlar bu renkte olacaktır.</p>
                      </div>
                      
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                          <Palette size={16} /> Şirket İsmi Rengi
                        </Label>
                        <div className="flex flex-wrap gap-3">
                          <div className="relative">
                            <input
                              type="color"
                              value={businessNameColor}
                              onChange={(e) => setBusinessNameColor(e.target.value)}
                              className="w-10 h-10 rounded-full border-2 border-slate-300 dark:border-slate-700 cursor-pointer overflow-hidden p-0"
                              title="Şirket İsmi Rengi Seç"
                            />
                          </div>
                          <p className="text-xs text-slate-500 flex items-center">Müşteri menüsünde logonuzun yanındaki şirket isminin rengi.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={isSavingSettings}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                      >
                        {isSavingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                        <Save size={16} className="ml-2" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Logo Upload Card */}
              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <ImageIcon className="text-orange-500" size={24} />
                    İşletme Logosu
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-6 hover:bg-slate-100 dark:bg-slate-800/50 transition-colors">
                    {logoPreview ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="relative group">
                          <img src={logoPreview} alt="Logo Preview" className="h-32 w-auto object-contain bg-white rounded-lg p-2" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg">
                            <Label htmlFor="logo-upload" className="cursor-pointer text-slate-900 dark:text-white flex flex-col items-center">
                              <Upload size={24} />
                              <span className="text-xs mt-1">Değiştir</span>
                            </Label>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-2">Maksimum boyut: 2MB (Önerilen: 500x500px)</span>
                      </div>
                    ) : (
                      <Label htmlFor="logo-upload" className="cursor-pointer flex flex-col items-center text-slate-600 dark:text-slate-400 hover:text-orange-400 transition-colors">
                        <div className="h-16 w-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                          <ImageIcon size={32} />
                        </div>
                        <span>Logo Seç veya Sürükle</span>
                        <span className="text-xs font-bold text-orange-400 mt-2 bg-orange-400/10 px-2 py-1 rounded">Maksimum boyut: 2MB (PNG, JPG)</span>
                      </Label>
                    )}
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                  </div>
                  <Button
                    onClick={handleLogoUpload}
                    disabled={!logoBase64 || isUploadingLogo}
                    className="w-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-600 text-slate-900 dark:text-white font-bold border border-slate-600"
                  >
                    {isUploadingLogo ? 'Yükleniyor...' : 'Logoyu Güncelle'}
                    <Upload size={16} className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          <TabsContent value="pos" className="space-y-6">
            {/* POS Settings Card */}
            <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <CreditCard className="text-orange-500" size={24} />
                  Yazar Kasa ve POS Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">Yazar Kasa / POS Markanız</Label>
                      <select
                        value={posType}
                        onChange={(e) => setPosType(e.target.value)}
                        className="w-full h-10 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="harici">Harici / Manuel POS (Varsayılan)</option>
                        <option value="techpos">BKM TechPOS (IP Tabanlı)</option>
                        <option value="ingenico">Ingenico Yazar Kasa POS</option>
                        <option value="beko">Beko / Hugin Yazar Kasa POS</option>
                      </select>
                      <p className="text-xs text-slate-500">
                        {posType === 'harici' && 'Adisyon yazılımdan kapanır, tutarı kasiyer yazar kasaya eliyle girer.'}
                        {posType === 'techpos' && 'BKM TechPOS standartlarını destekleyen tüm yeni nesil yazar kasa ve banka POS cihazları.'}
                        {posType === 'ingenico' && 'Ingenico Move/5000F, Desk/5000 ve masaüstü ECR uyumlu seriler.'}
                        {posType === 'beko' && 'Beko 300TR, Hugin T300 ve USB/COM Port bağlantılı yazar kasalar.'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">Cihaz IP Adresi / Terminal ID</Label>
                      <Input
                        type="text"
                        placeholder={posType === 'beko' ? 'USB / COM Port Bağlantısı' : 'Örn: 192.168.1.50'}
                        value={posIp}
                        onChange={(e) => setPosIp(e.target.value)}
                        disabled={posType === 'harici' || posType === 'beko'}
                        className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-50"
                      />
                      <p className="text-xs text-slate-500">
                        💡 Önemli: Otomatik tutar gönderiminin çalışması için POS cihazınızın SIM kart ile değil, kasa bilgisayarınızla aynı Wi-Fi / Ethernet ağına bağlı olduğundan emin olun.
                      </p>
                    </div>
                  </div>

                  {posType === 'beko' && (
                    <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-xl flex items-center justify-between">
                      <p className="text-sm text-orange-400">
                        💡 Beko / Hugin cihazınızın bilgisayarla haberleşebilmesi için resmi ECR Aracı Servis uygulamasını bilgisayarınıza kurmanız gereklidir.
                      </p>
                      <Button variant="outline" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white shrink-0 ml-4" onClick={() => alert('Beko ECR Service yükleyici (v1.4) indiriliyor...')}>
                        ECR Servisini İndir
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
                    <Button
                      type="button"
                      onClick={handlePOSTest}
                      disabled={isTestingPOS}
                      className="bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-white font-bold border border-slate-300 dark:border-slate-700"
                    >
                      <Wifi size={16} className="mr-2 text-orange-500" />
                      {isTestingPOS ? 'Bağlantı Sınanıyor...' : 'Bağlantıyı Sına / Test Et'}
                    </Button>

                    <Button
                      type="button"
                      onClick={handleSettingsSave}
                      disabled={isSavingSettings}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                    >
                      {isSavingSettings ? 'Kaydediliyor...' : 'POS Ayarlarını Kaydet'}
                      <Save size={16} className="ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="password" className="space-y-6">
            {/* Change Password Card */}
            <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Lock className="text-orange-500" size={24} />
                  Şifre Değiştir
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 dark:text-slate-300">Mevcut Şifreniz</Label>
                    <Input
                      type="password"
                      placeholder="Mevcut şifrenizi girin"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 dark:text-slate-300">Yeni Şifreniz</Label>
                    <Input
                      type="password"
                      placeholder="En az 6 karakter"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 dark:text-slate-300">Yeni Şifreniz (Tekrar)</Label>
                    <Input
                      type="password"
                      placeholder="Yeni şifrenizi tekrar girin"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                    className="w-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-600 text-slate-900 dark:text-white font-bold mt-2 border border-slate-600"
                  >
                    {isChangingPassword ? 'Değiştiriliyor...' : 'Şifreyi Güncelle'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="coupons" className="space-y-6">
            {/* Coupons Card */}
            <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Ticket className="text-orange-500" size={24} />
                  İndirim Kuponları
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Create Coupon */}
                  <form onSubmit={handleCreateCoupon} className="space-y-4 border-r border-slate-200 dark:border-slate-800 pr-4">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Yeni Kupon Oluştur</h3>
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">Kupon Kodu</Label>
                      <Input
                        type="text"
                        placeholder="Örn: YAZ10"
                        value={newCouponCode}
                        onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                        className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 uppercase"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700 dark:text-slate-300">İndirim Tipi</Label>
                        <select 
                          className="flex h-10 w-full rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                          value={newCouponType}
                          onChange={(e) => setNewCouponType(e.target.value)}
                        >
                          <option value="percentage">Yüzde (%)</option>
                          <option value="fixed">Sabit Tutar (₺)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 dark:text-slate-300">Değer</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={newCouponType === 'percentage' ? "10" : "50"}
                          value={newCouponValue}
                          onChange={(e) => setNewCouponValue(e.target.value)}
                          className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">Son Kullanma Tarihi (İsteğe Bağlı)</Label>
                      <Input
                        type="datetime-local"
                        value={newCouponExpiry}
                        onChange={(e) => setNewCouponExpiry(e.target.value)}
                        className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 dark:text-slate-300">Maksimum Kullanım Sayısı (İsteğe Bağlı)</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Sınırsız"
                        value={newCouponMaxUses}
                        onChange={(e) => setNewCouponMaxUses(e.target.value)}
                        className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isCreatingCoupon}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold"
                    >
                      {isCreatingCoupon ? 'Oluşturuluyor...' : 'Kupon Oluştur'}
                    </Button>
                  </form>

                  {/* List Coupons */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Aktif Kuponlar</h3>
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                      {coupons.length === 0 ? (
                        <p className="text-slate-500 text-sm">Aktif kupon bulunmuyor.</p>
                      ) : (
                        coupons.map(coupon => (
                          <div key={coupon.id} className="flex items-center justify-between bg-slate-100 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-300 dark:border-slate-700">
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-bold text-orange-400">{coupon.code}</span>
                                <span className="text-xs bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-700">
                                  {coupon.discount_type === 'percentage' ? `%${coupon.discount_value}` : `${coupon.discount_value}₺`}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                {coupon.expires_at ? `Son: ${new Date(coupon.expires_at).toLocaleString('tr-TR')}` : 'Süresiz'}
                                {coupon.max_uses ? ` · ${coupon.used_count || 0}/${coupon.max_uses} kullanım` : ' · Sınırsız kullanım'}
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteCoupon(coupon.id)}
                              className="text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-red-400/10"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Package className="text-orange-500" size={24} />
                  Akıllı Stok ve Depo Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-xl">
                  <h3 className="text-orange-400 font-bold mb-1">Stok & Hammadde Yönetimi Altyapısı (v0.0.2)</h3>
                  <p className="text-slate-600 dark:text-slate-300 text-sm">
                    Bu ayarı aktif ettiğinizde sol menüye <strong>"Stok Yönetimi"</strong> sekmesi eklenir. Menü sayfasında ürünlerinize hammadde reçetesi (BOM) tanımlayabilir ve sipariş anında otomatik stok düşüşünü takip edebilirsiniz.
                  </p>
                </div>

                <form onSubmit={handleSettingsSave} className="space-y-6">
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div>
                      <Label className="text-base font-bold text-slate-900 dark:text-white block">
                        Stok & Depo Altyapısını Aktif Et
                      </Label>
                      <p className="text-sm text-slate-500">
                        Depo takibi yapmak istemiyorsanız bu ayarı kapalı tutabilirsiniz.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={inventoryEnabled} 
                        onChange={(e) => setInventoryEnabled(e.target.checked)}
                        className="sr-only peer" 
                      />
                      <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSavingSettings}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-bold h-11 px-8"
                  >
                    <Save size={18} className="mr-2" />
                    {isSavingSettings ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
